import { useEffect, useState } from 'react';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { BasePage, type BasePageProps } from './BasePage';
import warning, { type LogArgument } from '../../utils/logger';
import { EmptyState } from '../emptyState/EmptyState';

type ErrorPageProps = {
    tryAgain?: () => void;
    error?: LogArgument;
} & Omit<BasePageProps, 'children' | 'title'>;

/**
 * The `tryAgain` argument is optional and is not needed if you are manually rendering this page.
 * This function will be provided when an error is detected by an ErrorBoundary,
 * which is wrapped around each element/page served by the router in the `PageContainer`.
 * The `tryAgain` function is sourced from the `react-error-boundary` library.
 *
 * The `error` argument is optional. If it is not provided, and you are manually rendering the page,
 * ensure that you have logged the error before displaying this page.
 * If you do provide the error, it will be logged to Rollbar, and the resulting Rollbar UUID will be
 * shown on the page.
 */
export function ErrorPage({ tryAgain, error, ...basePageProps }: ErrorPageProps): JSX.Element {
    const [rollbarUuid, setRollbarUuid] = useState<string | undefined>();
    const showTryAgainButton = Boolean(tryAgain);

    useEffect(() => {
        if (typeof error === 'undefined') {
            return;
        }

        warning(error, null, (uuid) => {
            setRollbarUuid(uuid);
        });
    }, [error]);

    return (
        <BasePage title={gettext('ERROR_PAGE_TITLE')} {...basePageProps}>
            <EmptyState
                variant="featured"
                iconFontAwesome={faTriangleExclamation}
                description={gettext('ERROR_PAGE_BODY')}
                actionButtonText={gettext('TRY_AGAIN')}
                actionButtonMethod={tryAgain}
                showActionButton={showTryAgainButton}
            />
            {rollbarUuid && (
                <p>
                    <small>
                        {gettext('REFERENCE_CODE')}: {rollbarUuid}
                    </small>
                </p>
            )}
        </BasePage>
    );
}
